import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import { TrackedJob, JobsContextType } from "../types/jobTypes";
import { AsyncJobStatusResponse } from "../types/compassAITypes";

// ─── App state ───────────────────────────────────────────────

interface App {
  authorisation: Authorisation;
}

type Authorisation = {
  token: string;
};

export const baseAppState: App = {
  authorisation: {
    token: "",
  },
};

interface AppContextType {
  app: App;
  setApp: React.Dispatch<React.SetStateAction<App>>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const useApp = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error("useApp must be used within an AppProvider");
  }
  return context;
};

// ─── Jobs state ──────────────────────────────────────────────

const JobsContext = createContext<JobsContextType | null>(null);

export const useJobs = (): JobsContextType => {
  const context = useContext(JobsContext);
  if (!context) {
    throw new Error("useJobs must be used within an AppProvider");
  }
  return context;
};

// ─── Combined provider ──────────────────────────────────────

export const AppProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  // App
  const [app, setApp] = useState<App>(baseAppState);

  // Jobs
  const [jobs, setJobs] = useState<TrackedJob[]>([]);

  const addJob = useCallback((id: string, name: string): void => {
    setJobs((prev) => {
      if (prev.some((j) => j.id === id)) return prev;
      return [
        {
          id,
          name,
          status: "PENDING",
          progress: 0,
          stepLabel: "Starting…",
          startedAt: Date.now(),
        },
        ...prev,
      ];
    });
  }, []);

  const updateJob = useCallback(
    (id: string, updates: Partial<Omit<TrackedJob, "id">>): void => {
      setJobs((prev) =>
        prev.map((job) => {
          if (job.id !== id) return job;
          const updated = { ...job, ...updates };
          if (updates.status === "COMPLETED" || updates.status === "FAILED") {
            updated.completedAt = Date.now();
          }
          return updated;
        }),
      );
    },
    [],
  );

  const removeJob = useCallback((id: string): void => {
    setJobs((prev) => prev.filter((j) => j.id !== id));
  }, []);

  const clearCompleted = useCallback((): void => {
    setJobs((prev) =>
      prev.filter((j) => j.status !== "COMPLETED" && j.status !== "FAILED"),
    );
  }, []);

  const JOB_TYPE_LABELS: Record<string, string> = {
    BATCH_LINKAGE: "Batch Linkage",
    ENGAGEMENT_EXTRACTION: "Engagement Extraction",
    ENGAGEMENT_ASSESSMENT: "Engagement Assessment",
    EMAIL_AGENT: "Email Agent",
    PREPARE_PORTFOLIO_NEWS: "Company News",
  };

  const syncFromServer = useCallback(
    (serverJobs: AsyncJobStatusResponse[]): void => {
      setJobs((prev) => {
        const existingById = new Map(prev.map((j) => [j.id, j]));

        const merged: TrackedJob[] = serverJobs.map((sj) => {
          const existing = existingById.get(sj.job_id);
          const progress =
            sj.total_steps && sj.total_steps > 0
              ? Math.round(((sj.current_step ?? 0) / sj.total_steps) * 100)
              : sj.status === "COMPLETED"
                ? 100
                : 0;

          return {
            id: sj.job_id,
            name:
              existing?.name ??
              (sj.job_type
                ? (JOB_TYPE_LABELS[sj.job_type] ?? sj.job_type)
                : "Job"),
            status: sj.status,
            progress,
            stepLabel:
              sj.step_label ?? (sj.status === "COMPLETED" ? "Done" : ""),
            startedAt: existing?.startedAt ?? Date.now(),
            completedAt:
              sj.status === "COMPLETED" || sj.status === "FAILED"
                ? (existing?.completedAt ?? Date.now())
                : undefined,
            error: sj.error_message ?? undefined,
            jobType: sj.job_type ?? existing?.jobType ?? null,
            resultPayload: sj.result_payload ?? existing?.resultPayload ?? null,
          };
        });

        // Keep locally-added jobs that aren't on the server yet
        const serverIds = new Set(serverJobs.map((sj) => sj.job_id));
        const localOnly = prev.filter(
          (j) =>
            !serverIds.has(j.id) &&
            (j.status === "PENDING" || j.status === "RUNNING"),
        );

        return [...localOnly, ...merged];
      });
    },
    [],
  );

  const activeJobs = useMemo(
    () => jobs.filter((j) => j.status === "PENDING" || j.status === "RUNNING"),
    [jobs],
  );

  // Sort: active jobs first, then by startedAt descending
  const sortedJobs = useMemo(() => {
    const isActive = (j: TrackedJob): boolean =>
      j.status === "PENDING" || j.status === "RUNNING";
    return [...jobs].sort((a, b) => {
      const aActive = isActive(a) ? 0 : 1;
      const bActive = isActive(b) ? 0 : 1;
      if (aActive !== bActive) return aActive - bActive;
      return b.startedAt - a.startedAt;
    });
  }, [jobs]);

  const jobsValue = useMemo(
    () => ({
      jobs: sortedJobs,
      addJob,
      updateJob,
      removeJob,
      clearCompleted,
      syncFromServer,
      hasActiveJobs: activeJobs.length > 0,
      activeJobCount: activeJobs.length,
    }),
    [
      sortedJobs,
      addJob,
      updateJob,
      removeJob,
      clearCompleted,
      syncFromServer,
      activeJobs,
    ],
  );

  return (
    <AppContext.Provider value={{ app, setApp }}>
      <JobsContext.Provider value={jobsValue}>{children}</JobsContext.Provider>
    </AppContext.Provider>
  );
};
